import type { TrackerPrototype } from '@racemap/sdk/schema/trackers';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { TRACKER_TYPES } from '@racemap/utilities/consts/trackers';
import { Flex } from 'antd';
import type { FC } from 'react';
import { toast } from 'react-toastify';
import trackerTypes from '../../../misc/tracker_types';
import { FormInput } from '../../FormComponents';
import { AddableDeviceChooser } from './AddableDeviceChooser';
import { TrackerTypeSelect } from './TrackerTypeSelect';

interface Props {
  value: TrackerPrototype;
  onChange: (tracker: TrackerPrototype) => void;
  isPredefinedTracker: boolean;
}

export const getTrackerPrototype = (
  trackerType: TRACKER_TYPES | null,
  trackerId?: string | null,
  trackerName?: string | null,
): TrackerPrototype => {
  const deviceKey = trackerType ? trackerType : TRACKER_TYPES.GL320LTEMG;

  const device = trackerTypes[deviceKey];

  return {
    trackerId: trackerId || device.generateAppId(),
    trackerType: trackerType || deviceKey,
    trackerName: trackerName || '',
  };
};

export const AddTrackerForm: FC<Props> = ({ value, onChange, isPredefinedTracker }) => {
  const { trackerId, trackerType, trackerName } = value;
  const selectors = TestSelectors.admin.trackers.addTrackerModal;

  const deviceTypeInfo = trackerTypes[trackerType];
  const trackerIdInputProps = deviceTypeInfo?.fixedId
    ? {
        readOnly: false,
        required: true,
        pattern: `^\\d{${deviceTypeInfo.idLength}}`,
      }
    : {
        readOnly: true,
        required: false,
      };

  const onTrackerIDChange = (newTrackerId: string) => {
    if (typeof newTrackerId !== 'string' || newTrackerId.match(/\D/gm) != null) {
      toast.warn('Wrong tracker id. Only number based ids are possible.');
      return;
    }

    onChange({
      ...value,
      trackerId: newTrackerId,
    });
  };

  const handleTrackerTypeChange = (newTrackerTypeKey: string | null) => {
    const trackerType = Object.values(TRACKER_TYPES).find((k) => k === newTrackerTypeKey);
    if (trackerType == null) return;

    const newDeviceType = trackerTypes[trackerType];
    onChange({
      trackerType,
      trackerId: newDeviceType.generateAppId(),
      trackerName: '',
    });
  };

  const handleTrackerNameChange = (newTrackerName: string) => {
    onChange({
      ...value,
      trackerName: newTrackerName,
    });
  };

  return (
    <Flex gap="20px" wrap>
      <TrackerTypeSelect
        value={trackerType}
        onChange={handleTrackerTypeChange}
        disabled={isPredefinedTracker}
      />
      {deviceTypeInfo.isRRDevice || deviceTypeInfo.isInsidersDevice ? (
        <AddableDeviceChooser value={value} onChange={onChange} />
      ) : (
        <FormInput
          id={selectors.idInput.toString()}
          label="Tracker ID (often the IMEI):"
          onChange={onTrackerIDChange}
          value={trackerId}
          style={{ width: 200 }}
          disabled={isPredefinedTracker}
          {...trackerIdInputProps}
        />
      )}
      <FormInput
        id={selectors.nameInput.toString()}
        label="Name:"
        onChange={(newValue) => handleTrackerNameChange(String(newValue))}
        value={trackerName}
        style={{ width: 200 }}
      />
    </Flex>
  );
};
